// Reports view — "7-day rolling intake vs bodyweight".
// Same series shape as SummaryHero's own chart (buildChartSeries), same
// dual-axis composed-chart approach, but without the training-phase bands —
// the mockup for this chart doesn't have them (Hero's own chart still does).

import React, { useMemo, memo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

import { toDisplayWeight, evenlySpacedTicks, CHART_THEME, kcalChartDomain, weightChartDomain } from "./viewTokens";
import { ChartLegend, TooltipShell, tdeeReferenceLineProps } from "./viewAtoms";
import { useCenteredTooltip } from "../../../../../hooks/useCenteredTooltip";

const INTAKE_COLOR = "#E8A22D";
const TARGET_COLOR = "var(--white-50)";
const WEIGHT_COLOR = "var(--tracking)"; // #4e81f2
// TARGET_COLOR (var(--white-50)) is theme-aware — flips to a DARK gray in
// light theme, which is correct on the Line (drawn on the theme-flipping
// card) but wrong inside the tooltip: .np-hero__tooltip's background is a
// fixed #1a1e25, never flips with the theme, so a dark-in-light-theme color
// there read as dark-on-dark, nearly invisible. This is the dark-theme
// value of --white-50 hardcoded, since the tooltip itself never changes.
const TARGET_TOOLTIP_COLOR = "rgba(255, 255, 255, 0.5)";
// Theme-aware, shared with SummaryHero.js — see that file's comment (or
// CHART_THEME's own, viewTokens.js) for the full "why".
const { tick: CHART_TICK, tickFontSize: CHART_TICK_SIZE, grid: CHART_GRID } = CHART_THEME;

const TICK_COUNT = 6;

// Reuses Hero's own chart.* copy verbatim (SummaryHero.js) — same series
// shape, same labels ("Intake (7d avg)", "TDEE", axis units...), so this
// avoids a second English string that could drift from Hero's over time.
// Only the card title (below, in ReportsView.js) is new/reports-specific.
const r = (t, key, opts) => t(`nutritionPage.progress.hero.chart.${key}`, opts);
// Legend-only, shorter labels ("Intake"/"Target" instead of "Intake (7d avg)")
// — Reports-specific, does NOT touch r()'s shared copy above (still used by
// this chart's own tooltip and by SummaryHero.js's legend/tooltip).
const ri = (t, key, opts) => t(`nutritionPage.progress.reports.intakeChart.${key}`, opts);

/* ---- custom tooltip ----------------------------------------------------- */
// Reuses .np-hero__tooltip* classes (SummaryHero.js) — same floating dark
// surface everywhere in Nutrition Progress, not just Hero's own chart.

const IntakeTooltip = ({ active, payload, weightUnit, onMeasureWidth }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <TooltipShell active={active} payload={payload} onMeasureWidth={onMeasureWidth}>
      {(pt) => {
        const fullDate = pt?.utcDate ? moment(String(pt.utcDate), "YYYYMMDD").format("MMM D, YYYY") : pt?.name;
        return (
          <>
            <div className="np-hero__tooltip-date">{fullDate}</div>
            {pt?.intake != null && (
              <div className="np-hero__tooltip-row" style={{ color: INTAKE_COLOR }}>
                <span>{r(t, "intake")}:</span>
                <span>{pt.intake.toLocaleString()} kCal</span>
              </div>
            )}
            {pt?.target != null && (
              <div className="np-hero__tooltip-row" style={{ color: TARGET_TOOLTIP_COLOR }}>
                <span>{r(t, "target")}:</span>
                <span>{pt.target.toLocaleString()} kCal</span>
              </div>
            )}
            {pt?.weight != null && (
              <div className="np-hero__tooltip-row" style={{ color: WEIGHT_COLOR }}>
                <span>{r(t, "bodyweight")}:</span>
                <span>{pt.weight} {weightUnit}</span>
              </div>
            )}
          </>
        );
      }}
    </TooltipShell>
  );
};

IntakeTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  weightUnit: PropTypes.string,
  onMeasureWidth: PropTypes.func,
};

/* ---- chart --------------------------------------------------------------- */

export const ReportsIntakeChart = memo(({ series, workingExpenditure, decoupled, staleData, weeklyChange, units }) => {
  const { t } = useTranslation("athlete-portal");
  const tdeeLineProps = tdeeReferenceLineProps({ workingExpenditure, decoupled, staleData, weeklyChange, yAxisId: "kcal", t });
  const isImperial = units === "imperial";
  const weightUnit = isImperial ? "lb" : "kg";
  const weightAxisKey = isImperial ? "lbAxis" : "kgAxis";

  // Bodyweight always arrives in kg from buildChartSeries (see
  // toDisplayWeight's own comment) — only the display layer converts. Always
  // routed through toDisplayWeight (not just when isImperial) since it also
  // rounds to 1 decimal even with no unit conversion — skipping it for
  // metric left the raw, unrounded kg weigh-in in the tooltip.
  const displaySeries = useMemo(
    () => series.map((pt) => ({ ...pt, weight: toDisplayWeight(pt.weight, isImperial) })),
    [series, isImperial]
  );

  // Tick indices for the X axis.
  const tickIndices = useMemo(
    () => evenlySpacedTicks(displaySeries.length - 1, TICK_COUNT),
    [displaySeries]
  );

  // Zoom the kcal axis to the data (not Recharts' 0-anchored default) while
  // guaranteeing every drawn line fits inside it — see kcalChartDomain
  // (viewTokens.js) for why the trim can't be allowed to clip a plotted line.
  const kcalDomain = useMemo(
    () => kcalChartDomain(displaySeries, workingExpenditure),
    [displaySeries, workingExpenditure]
  );

  // Same zoom for the weight axis — see SummaryHeroChart.js's own comment
  // for the full "why" (defaulted to Recharts' 0-anchored domain too).
  const weightDomain = useMemo(
    () => weightChartDomain(displaySeries.map((pt) => pt.weight)),
    [displaySeries]
  );
  // Fallback when computeAxisDomain bails — NOT Recharts' raw default, same
  // reasoning as SummaryHeroChart.js's own naiveWeightMin/Max.
  const [naiveWeightMin, naiveWeightMax] = useMemo(() => {
    const vals = displaySeries.map((pt) => pt.weight).filter((v) => v != null && Number.isFinite(v));
    if (!vals.length) return [0, 1];
    return [Math.floor(Math.min(...vals) - 1), Math.ceil(Math.max(...vals) + 1)];
  }, [displaySeries]);

  // No weigh-ins at all in the range — see SummaryHeroChart.js's own
  // comment for the full "why" (suppresses the axis/Line/legend entry
  // instead of showing a fake 0/1-tick scale).
  const hasWeightData = useMemo(
    () => displaySeries.some((pt) => pt.weight != null),
    [displaySeries]
  );

  // Legend reading order/labels are intentionally shorter than the <Line>
  // declarations' own `name` (which the tooltip and SummaryHero.js's legend
  // still use as-is) — see ri()'s comment above.
  const legendPayload = useMemo(
    () => [
      { value: ri(t, "legendIntake"), color: INTAKE_COLOR, dataKey: "intake" },
      { value: ri(t, "legendTarget"), color: TARGET_COLOR, dataKey: "target" },
      ...(hasWeightData ? [{ value: r(t, "bodyweight"), color: WEIGHT_COLOR, dataKey: "weight" }] : []),
    ],
    [t, hasWeightData]
  );

  // Manual tooltip centering — see useCenteredTooltip's own header comment
  // for the full "why" (Recharts doesn't do this by default).
  const { containerRef, tooltipPosition, onMeasureWidth, chartHandlers } = useCenteredTooltip({
    initialWidth: 180,
  });

  return (
    <div ref={containerRef}>
      <ResponsiveContainer width="99%" height={280}>
        <ComposedChart
          data={displaySeries}
          margin={{ top: 36, right: 52, bottom: 0, left: 0 }}
          {...chartHandlers}
        >
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey="idx"
            type="number"
            domain={[0, Math.max(displaySeries.length - 1, 1)]}
            ticks={tickIndices}
            tickFormatter={(i) => displaySeries[i]?.name ?? ""}
            tick={{ fontSize: CHART_TICK_SIZE, fill: CHART_TICK }}
            tickLine={false}
            axisLine={false}
            padding={{ left: 32, right: 32 }}
          />
          <YAxis
            yAxisId="kcal"
            domain={kcalDomain ?? undefined}
            allowDataOverflow={kcalDomain != null}
            tick={{ fontSize: CHART_TICK_SIZE, fill: CHART_TICK }}
            tickLine={false}
            axisLine={false}
            width={44}
            label={{ value: r(t, "kcalAxis"), position: "top", offset: 16, fontSize: 11, fontWeight: 700, fill: "var(--primary)" }}
          />
          {hasWeightData && (
            <YAxis
              yAxisId="weight"
              orientation="right"
              domain={weightDomain ?? [naiveWeightMin, naiveWeightMax]}
              allowDataOverflow={weightDomain != null}
              tick={{ fontSize: CHART_TICK_SIZE, fill: CHART_TICK }}
              tickLine={false}
              axisLine={false}
              width={36}
              label={{ value: r(t, weightAxisKey), position: "top", offset: 16, fontSize: 11, fontWeight: 700, fill: "var(--primary)" }}
            />
          )}
          <Tooltip
            content={<IntakeTooltip weightUnit={weightUnit} onMeasureWidth={onMeasureWidth} />}
            cursor={{ stroke: "var(--primary)", strokeWidth: 1 }}
            position={tooltipPosition}
          />
          <Legend
            content={<ChartLegend coloredText />}
            payload={legendPayload}
            wrapperStyle={{ paddingTop: 8 }}
          />

          <Line
            name={r(t, "intake")}
            yAxisId="kcal"
            dataKey="intake"
            stroke={INTAKE_COLOR}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5, strokeWidth: 0 }}
            connectNulls
            type="monotone"
            isAnimationActive={false}
          />
          <Line
            name={r(t, "target")}
            yAxisId="kcal"
            dataKey="target"
            stroke={TARGET_COLOR}
            strokeWidth={1.5}
            strokeDasharray="4 2"
            dot={false}
            activeDot={{ r: 5, strokeWidth: 0 }}
            connectNulls
            type="monotone"
            isAnimationActive={false}
          />
          {hasWeightData && (
            <Line
              name={r(t, "bodyweight")}
              yAxisId="weight"
              dataKey="weight"
              stroke={WEIGHT_COLOR}
              strokeWidth={1.5}
              dot={{ r: 2.5, fill: WEIGHT_COLOR, strokeWidth: 0 }}
              activeDot={{ r: 5, strokeWidth: 0 }}
              connectNulls
              type="monotone"
              isAnimationActive={false}
            />
          )}

          {/* Declared last (not right after Legend) so it paints on top of
              the 3 <Line>s above — SVG has no z-index, later-declared
              siblings paint over earlier ones, and the decoupled tooltip's
              foreignObject was getting drawn under the lines when this was
              declared first. */}
          {tdeeLineProps && <ReferenceLine {...tdeeLineProps} />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
});

ReportsIntakeChart.propTypes = {
  series: PropTypes.array.isRequired,
  workingExpenditure: PropTypes.number,
  decoupled: PropTypes.bool,
  staleData: PropTypes.bool,
  weeklyChange: PropTypes.number,
  units: PropTypes.string,
};

export default ReportsIntakeChart;
